import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, Observable, of, switchMap, throwError} from 'rxjs';
import {PeoApiModel} from '../model/peo-api.model';
import {environment} from '../../environment';
import {PeoApiCredentialModel} from '../model/peo-api-credential.model';
import {PeoApiCredentialTestModel} from '../model/peo-api-credential-test.model';

/**
 * @class
 * @description This class provides methods to interact with the PEO API.
 * @export
 * @name PeoService
 * @service
 */
@Injectable({

  // We'll want this service to be provided as a singleton.
  providedIn: 'root'
})
export class PeoService {

  /**
   * @constructor
   * @description This method instantiates a new instance of our PEO service provider with injected dependencies.
   * @memberof PeoService
   * @name PeoService#constructor()
   * @param {HttpClient} httpClient The injected instance of our HTTP Client service provider.
   */
  public constructor(
    /**
     * @description This property contains the injected instance of our HTTP Client service provider.
     * @memberof PeoService
     * @name PeoService#httpClient
     * @private
     * @readonly
     * @type {HttpClient}
     */
    private readonly httpClient: HttpClient,
  ) {
  }

  /**
   * @async
   * @description This method asynchronously lists the PEOs from the .NET API.
   * @memberof PeoService
   * @method
   * @name PeoService#list()
   * @public
   * @returns {Observable<PeoApiModel[]>} An observable containing the response from the .NET Backend API.
   * @throws {Error} When no current session exists.
   */
  public list(): Observable<PeoApiModel[]> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get<PeoApiModel[]>(environment.normalizedApiUrl('/api/administrator/peo'), {

      // Set the with credentials flag into the request options.
      withCredentials: true
    });
  }

  /**
   * @async
   * @description This method asynchronously loads all PEO API Credentials from the .NET API.
   * @memberof PeoService
   * @method
   * @name PeoService#listApiCredentials()
   * @public
   * @returns {Observable<PeoApiCredentialModel[]>} An observable containing the response from the .NET Backend API.
   */
  public listApiCredentials(): Observable<PeoApiCredentialModel[]> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient
      .get<PeoApiCredentialModel[]>(environment.normalizedApiUrl('/api/administrator/peo/api-credential'), {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @async
   * @description This method asynchronously loads the PEO from the .NET API.
   * @memberof PeoService
   * @method
   * @name PeoService#load()
   * @param {string} peoId The unique identifier of the PEO to load.
   * @public
   * @returns {Observable<PeoApiModel | null>} An observable containing the response from the .NET Backend API.
   */
  public load(peoId: string): Observable<PeoApiModel | null> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get<PeoApiModel>(environment
      .normalizedApiUrl('/api/administrator/peo/:peoId', {peoId}), {

      // Set the with credentials flag into the request options.
      withCredentials: true
    }).pipe(catchError((e: HttpErrorResponse): Observable<null> => e.status === 404
      ? of(null)
      : throwError((): HttpErrorResponse => e)));
  }

  /**
   * @async
   * @description This method asynchronously loads the API credentials for the specified PEO.
   * @memberof PeoService
   * @method
   * @name PeoService#loadApiCredential()
   * @param {string} peoId The unique identifier of the PEO to load the API credentials for.
   * @public
   * @returns {Observable<PeoApiCredentialModel | null>} An observable containing the response from the .NET Backend API.
   */
  public loadApiCredential(peoId: string): Observable<PeoApiCredentialModel | null> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get<PeoApiCredentialModel>(environment
      .normalizedApiUrl('/api/administrator/peo/:peoId/api-credential', {peoId}), {

      // Set the with credentials flag into the request options.
      withCredentials: true
    }).pipe(catchError((e: HttpErrorResponse): Observable<null> => e.status === 404
      ? of(null)
      : throwError((): HttpErrorResponse => e)));
  }

  /**
   * @async
   * @description This method asynchronously maps the PEOs from the .NET API then maps them for use in a view.
   * @memberof PeoService
   * @method
   * @name PeoService#map()
   * @public
   * @returns {Observable<{[key: string]: PeoApiModel}>} An observable containing the response from the .NET Backend API.
   */
  public map(): Observable<{ [key: string]: PeoApiModel }> {

    // Load the list of PEOs.
    return this.list().pipe(switchMap((peoList: PeoApiModel[]): Observable<{ [key: string]: PeoApiModel }> => {

      // Define our PEO map.
      const peoMap: { [key: string]: PeoApiModel } = {};

      // Iterate over each PEO and add it to the map.
      peoList.forEach(peo => peoMap[peo.peoId] = peo);

      // Return the observable containing the PEO map.
      return of(peoMap);
    }));
  }

  /**
   * @async
   * @description This method asynchronously saves a PEO.
   * @memberof PeoService
   * @method
   * @name PeoService#save()
   * @param {PeoApiModel} model The PEO to save.
   * @public
   * @returns {Observable<PeoApiModel>} An observable containing the response from the .NET Backend API.
   */
  public save(model: PeoApiModel): Observable<PeoApiModel> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.post<PeoApiModel>(environment.normalizedApiUrl('/api/administrator/peo'), model, {

      // Set the with credentials flag into the request options.
      withCredentials: true
    });
  }

  /**
   * @async
   * @description This method asynchronously save a PEO's PrismHR API Credentials.
   * @memberof PeoService
   * @method
   * @name PeoService#saveApiCredentials()
   * @param {PeoApiCredentialModel} model The PrismHR API credentials to save.
   * @public
   * @returns {Observable<PeoApiCredentialModel>}
   */
  public saveApiCredentials(model: PeoApiCredentialModel): Observable<PeoApiCredentialModel> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient
      .post<PeoApiCredentialModel>(environment.normalizedApiUrl('/api/administrator/peo/api-credential'), model, {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @async
   * @description This method asynchronously tests a PEO's PrismHR API Credentials.
   * @memberof PeoService
   * @method
   * @name PeoService#testApiCredentials()
   * @param {PeoApiCredentialTestModel} model The PrismHR API Credentials to test.
   * @public
   * @returns {Observable<PeoApiCredentialTestModel>} An observable containing the response from the .NET Backend API.
   */
  public testApiCredentials(model: PeoApiCredentialModel): Observable<PeoApiCredentialTestModel> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient
      .post<PeoApiCredentialTestModel>(environment.normalizedApiUrl('/api/administrator/peo/api-credential/test'), model, {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @async
   * @description This method asynchronously tests a PEO's PrismHR API Credentials.
   * @memberof PeoService
   * @method
   * @name PeoService#testApiCredentialsForPeo()
   * @param {string} peoId The PEO ID to test the PrismHR API Credentials for.
   * @public
   * @returns {Observable<PeoApiCredentialTestModel>} An observable containing the response from the .NET Backend API.
   */
  public testApiCredentialsForPeo(peoId: string): Observable<PeoApiCredentialTestModel> {

    // Check the input argument then return the observable from the HTTP Client service provider.
    return this.httpClient.get<PeoApiCredentialTestModel>(environment
      .normalizedApiUrl('/api/administrator/peo/:peoId/api-credential/test', {peoId}), {withCredentials: true});
  }
}
